import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { ArrowLeft, FileText, Lock, X, CheckCircle, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import { useEmbeddedMode, postEmbeddedStepComplete } from '../hooks/useEmbeddedMode';

// Default NDA password (used as fallback)
const DEFAULT_NDA_PASSWORD = "OrbIsDemocracy1!";
const NDA_FORM_URL = "https://forms.wix.com/f/7281206853726372285";

const NDAPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, api, refreshUser } = useAuth();
  const embedded = useEmbeddedMode();
  const [showNDAForm, setShowNDAForm] = useState(false);
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const [password, setPassword] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [progressPassword, setProgressPassword] = useState(DEFAULT_NDA_PASSWORD);

  // Check if came from admin onboarding - check multiple sources
  const fromAdmin = location.state?.fromAdmin || 
    sessionStorage.getItem('admin_onboarding_return') === 'true' ||
    document.referrer.includes('/admin/onboarding');

  // Handle back navigation
  const handleBack = () => {
    // Clear the session flag when going back
    sessionStorage.removeItem('admin_onboarding_return');
    if (fromAdmin) {
      navigate('/admin/onboarding');
    } else {
      navigate('/');
    }
  };

  // Fetch current password from system settings
  useEffect(() => {
    const fetchPassword = async () => {
      try {
        const res = await api.get('/admin/onboarding/passwords');
        if (res.data?.step2c?.progress) {
          setProgressPassword(res.data.step2c.progress);
        }
      } catch (error) {
        // Use default password if fetch fails
        setProgressPassword(DEFAULT_NDA_PASSWORD);
      }
    };
    fetchPassword();
  }, [api]);

  // Check if already signed
  const isNDASigned = user?.step2c_nda_signed;

  const handleSignNDA = () => {
    setShowNDAForm(true);
  };

  const handleExitForm = () => {
    setShowNDAForm(false);
    setShowPasswordDialog(true);
  };

  const handleVerifyPassword = async () => {
    if (!password) {
      toast.error('Please enter the password');
      return;
    }

    setVerifying(true);
    
    // Verify against the progress password
    try {
      const res = await api.post('/auth/verify-onboarding-password', {
        step_id: 'step2c',
        password: password
      });
      
      if (res.data.valid) {
        await api.put('/auth/me', { step2c_nda_signed: true });
        await refreshUser();
        toast.success('NDA signed successfully!');
        setShowPasswordDialog(false);
        if (embedded) {
          postEmbeddedStepComplete('step2c_nda_signed');
        } else {
          navigate('/');
        }
      } else {
        toast.error('Incorrect password. Please check the confirmation page after signing the NDA.');
      }
    } catch (error) {
      // Fallback to local check if API not available
      if (password === progressPassword || password === DEFAULT_NDA_PASSWORD) {
        try {
          await api.put('/auth/me', { step2c_nda_signed: true });
          await refreshUser();
          toast.success('NDA signed successfully!');
          setShowPasswordDialog(false);
          if (embedded) {
            postEmbeddedStepComplete('step2c_nda_signed');
          } else {
            navigate('/');
          }
        } catch (e) {
          toast.error('Failed to update status. Please try again.');
        }
      } else {
        toast.error('Incorrect password. Please check the confirmation page after signing the NDA.');
      }
    }
    
    setVerifying(false);
  };

  const handleClosePasswordDialog = () => {
    setShowPasswordDialog(false);
    setPassword('');
  };

  // If already signed, show success state
  if (isNDASigned) {
    return (
      <div className={embedded ? "p-3 bg-white text-[#1c1a1c]" : "min-h-screen bg-gradient-to-b from-[#d4e4cf] via-[#e8ede6] to-[#c9d4c3] p-4 text-[#1c1a1c]"}>
        <div className="max-w-md mx-auto pt-6">
          {!embedded && (
            <div className="flex items-center gap-4 mb-6">
              <Button
                variant="ghost"
                size="icon"
                onClick={handleBack}
                className="rounded-full"
                data-testid="back-btn"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div>
                <h1 className="text-2xl font-bold text-[#1c1a1c]">NDA Status</h1>
                <p className="text-sm text-[#676767]">Non-Disclosure Agreement</p>
              </div>
            </div>
          )}

          <Card className="bg-white/90 border-0 shadow-lg">
            <CardContent className="p-6 text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <CheckCircle className="w-9 h-9 text-green-600" />
              </div>
              <h2 className="text-xl font-bold mb-1 text-[#1c1a1c]">NDA Signed</h2>
              <p className="text-sm text-[#676767]">
                You're bound by the terms of the Non-Disclosure Agreement.
              </p>
            </CardContent>
          </Card>

          {!embedded && (
            <Button
              className="w-full mt-4 bg-[#2C3E50] hover:bg-[#34495E] text-white h-12"
              onClick={() => navigate('/')}
            >
              Return to Onboarding
            </Button>
          )}
        </div>
      </div>
    );
  }

  // NDA Form Overlay (Full Screen Iframe) — also locked to light mode.
  // iter166 — When embedded inside the home-page popup, render JUST
  // the iframe with NO header bar. The user requested removing the
  // "Non-Disclosure Agreement / Exit & Enter Password" chrome — they
  // already have the popup's drag handle + close X above us.
  if (showNDAForm) {
    if (embedded) {
      return (
        <div className="fixed inset-0 bg-white flex flex-col">
          <iframe
            src={NDA_FORM_URL}
            className="w-full h-full flex-1 border-0"
            title="NDA Form"
            allow="camera; microphone"
          />
        </div>
      );
    }
    return (
      <div className="fixed inset-0 bg-white z-[100] flex flex-col">
        <div className="flex items-center justify-between p-3 border-b border-[#dfdfdf] bg-white">
          <div className="flex items-center gap-2">
            <FileText className="w-4 h-4 text-[#757150]" />
            <span className="text-sm font-semibold text-[#1c1a1c]">Non-Disclosure Agreement</span>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={handleExitForm}
            className="gap-2 h-8"
            data-testid="exit-nda-form-btn"
          >
            <X className="w-3.5 h-3.5" />
            Exit & Enter Password
          </Button>
        </div>

        <div className="flex-1 overflow-hidden">
          <iframe
            src={NDA_FORM_URL}
            className="w-full h-full border-0"
            title="NDA Form"
            allow="camera; microphone"
          />
        </div>
      </div>
    );
  }

  // Main NDA Page
  // iter166 — Force LIGHT MODE only on this page (no `dark:` classes).
  //   User reported the page rendering randomly in dark mode depending
  //   on system theme. Lock it down so signers always see the same
  //   contrast / readability.
  // Embedded layout: full-height flex column so the content fills the
  // popup with NO trailing whitespace — the Sign button is pinned to
  // the bottom, the body sits comfortably in the middle.
  if (embedded) {
    return (
      <div className="min-h-screen flex flex-col bg-white text-[#1c1a1c]">
        {/* Hero — fills the upper portion */}
        <div className="px-6 pt-8 pb-4 flex flex-col items-center text-center">
          <div className="w-16 h-16 bg-[#e8ede6] rounded-2xl flex items-center justify-center mb-4 shadow-sm">
            <FileText className="w-8 h-8 text-[#757150]" />
          </div>
          <h2 className="text-2xl font-bold text-[#1c1a1c]">Sign the NDA</h2>
        </div>

        {/* Body — agreement explanation + post-sign callout */}
        <div className="px-6 flex-1 flex flex-col justify-center">
          <div className="rounded-2xl bg-[#fafaf7] border border-[#ece9df] p-5 shadow-sm">
            <p className="text-[14.5px] text-[#3a3a3a] leading-[1.6]">
              Our <span className="font-semibold text-[#1c1a1c]">Non-Disclosure Agreement</span>{' '}
              allows us to share our proprietary information, business strategies, and other
              confidential materials with you. Please be sure to read the agreement carefully. An
              essential part of the agreement is that you may not divulge any information shared
              with you to any third party without first obtaining written consent from the Company.
            </p>

            <div className="my-4 h-px bg-[#ece9df]" />

            <p className="text-[14px] text-[#4a4a4a] leading-[1.55]">
              Questions about what you can or cannot disclose?
            </p>
            <a
              href="mailto:disclosures@collectiveorb.com"
              className="mt-1.5 inline-flex items-center gap-1.5 text-[13.5px] font-semibold text-[#757150] hover:text-[#5c5840] underline underline-offset-4 decoration-[#c9c4a8] hover:decoration-[#757150] transition-colors"
            >
              disclosures@collectiveorb.com
            </a>
          </div>

          {/* Post-sign callout — subtle olive accent left border */}
          <div className="mt-4 rounded-2xl bg-[#f5f5f0] border-l-[3px] border-[#757150] px-4 py-3.5 flex items-start gap-3">
            <div className="w-8 h-8 rounded-full bg-white flex items-center justify-center flex-shrink-0 shadow-sm">
              <Lock className="w-4 h-4 text-[#757150]" />
            </div>
            <div className="flex-1">
              <p className="text-[13.5px] font-semibold text-[#1c1a1c] mb-0.5">After signing</p>
              <p className="text-[12.5px] text-[#5a5a5a] leading-[1.5]">
                You'll receive a confirmation password.{' '}
                <span className="font-semibold text-[#1c1a1c]">Return to the home page</span> to
                enter the password and complete this step.
              </p>
            </div>
          </div>
        </div>

        {/* Footer — sticky Sign button + safe-area padding */}
        <div
          className="px-6 pt-4 pb-6 bg-white"
          style={{ paddingBottom: 'max(1.5rem, env(safe-area-inset-bottom))' }}
        >
          <Button
            className="w-full bg-[#757150] hover:bg-[#5c5840] text-white h-12 rounded-xl flex items-center justify-center gap-2 text-base shadow-sm"
            onClick={handleSignNDA}
            data-testid="sign-nda-btn"
          >
            <FileText className="w-4 h-4" />
            Sign the NDA
          </Button>
        </div>
      </div>
    );
  }

  // Non-embedded (standalone /nda route) — original layout preserved.
  return (
    <div className="min-h-screen bg-gradient-to-b from-[#d4e4cf] via-[#e8ede6] to-[#c9d4c3] p-4 pb-24 text-[#1c1a1c]">
      <div className="max-w-md mx-auto pt-2">
        <div className="flex items-center gap-4 mb-6">
          <Button
            variant="ghost"
            size="icon"
            onClick={handleBack}
            className="rounded-full"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-[#1c1a1c]">Non-Disclosure Agreement</h1>
            <p className="text-sm text-[#676767]">Protect our proprietary information</p>
          </div>
        </div>

        <Card className="bg-white/90 border-0 shadow-lg mb-4">
          <CardContent className="p-5">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-9 h-9 bg-[#e8ede6] rounded-lg flex items-center justify-center">
                <FileText className="w-4 h-4 text-[#757150]" />
              </div>
              <h2 className="text-lg font-bold text-[#1c1a1c]">Sign the NDA</h2>
            </div>

            <p className="text-sm text-[#4a4a4a] leading-snug mb-3">
              Sign our <span className="font-semibold text-[#1c1a1c]">Non-Disclosure Agreement</span> so we can share proprietary information with you. Once signed, you are <span className="font-semibold">legally bound</span> to keep all confidential materials private without written consent.
            </p>

            <div className="bg-[#f5f5f5] rounded-lg p-3 flex items-start gap-2.5">
              <Lock className="w-4 h-4 text-[#757150] flex-shrink-0 mt-0.5" />
              <p className="text-xs text-[#676767] leading-snug">
                After signing, you'll receive a confirmation password. <span className="font-semibold text-[#1c1a1c]">Return to the home page to enter the password</span> and complete this step.
              </p>
            </div>
          </CardContent>
        </Card>

        <Button
          className="w-full bg-[#2C3E50] hover:bg-[#34495E] text-white h-11 flex items-center justify-center gap-2"
          onClick={handleSignNDA}
          data-testid="sign-nda-btn"
        >
          <FileText className="w-4 h-4" />
          Sign the NDA
        </Button>

        <button
          onClick={() => setShowPasswordDialog(true)}
          className="w-full text-center mt-3 text-sm text-[#676767] hover:text-[#1c1a1c] underline"
          data-testid="already-signed-btn"
        >
          I've already signed - Enter password
        </button>
        <button
          onClick={() => navigate('/')}
          className="w-full text-center mt-2 text-sm text-[#676767] hover:text-[#1c1a1c]"
        >
          I'll do this later
        </button>
      </div>

      {/* Password Dialog — standalone-page only. */}
      <Dialog open={showPasswordDialog} onOpenChange={handleClosePasswordDialog}>
        <DialogContent className="max-w-sm w-[calc(100%-2rem)] p-0 overflow-hidden bg-white rounded-3xl left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
          <DialogHeader className="p-4 pb-2">
            <DialogTitle className="text-lg font-bold">Enter NDA Password</DialogTitle>
            <p className="text-xs text-muted-foreground mt-0.5">
              Enter the password shown on the confirmation page after signing the NDA.
            </p>
          </DialogHeader>

          <div className="px-4 pb-4 space-y-3">
            <div className="bg-[#f5f5f5] rounded-xl p-3">
              <div className="flex items-start gap-2 mb-2">
                <AlertCircle className="w-4 h-4 text-[#757150] flex-shrink-0 mt-0.5" />
                <p className="text-xs text-[#676767]">
                  If you haven't signed the NDA yet, click "Sign the NDA" button first.
                  The password will be displayed after you complete the form.
                </p>
              </div>

              <Label className="text-xs text-[#676767]">
                Confirmation Password
              </Label>
              <Input
                type="password"
                placeholder="Enter password from confirmation page"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-1.5 h-9 rounded-xl"
                data-testid="nda-password-input"
              />
            </div>

            <div className="flex gap-2">
              <Button
                variant="outline"
                className="flex-1 h-10 rounded-xl"
                onClick={handleClosePasswordDialog}
              >
                Cancel
              </Button>
              <Button
                className="flex-1 h-10 rounded-xl bg-[#2C3E50] hover:bg-[#34495E] text-white"
                onClick={handleVerifyPassword}
                disabled={verifying || !password}
                data-testid="verify-nda-password-btn"
              >
                {verifying ? 'Verifying...' : 'Verify & Complete'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default NDAPage;
